/**
 * HostsPage — Story 2.2 Host-Management-UI
 *
 * Referenz (AUTORITATIV): claude-design-import/hosts.html.
 *
 * AC-1: Tabellarische Liste mit Partnername, zugeordneten Hotspots,
 *       monatlichem Ertrag/GB und Auszahlungs-Status (StatusChip) —
 *       Daten aus hosts-/settlements-/host_split_rules-Endpunkten.
 * AC-2: Host-Detail als Drawer (ModalSheet) mit Bearbeitungsformular
 *       inkl. 422-Validierungs-Feedback (HostFormDrawer).
 * AC-3: Empty-State mit Call-to-Action zur Anlage des ersten Partners.
 *
 * Abweichung zur Vorlage (dokumentiert): hosts.html filtert nach einem
 * Host-Status „Aktiv/Pausiert“ — dieses Feld existiert im OpenAPI-Contract
 * (Host-Schema, Story 2.1) NICHT. Der Segment-Filter filtert deshalb nach
 * dem kontraktgestuetzten Auszahlungs-Status der juengsten Abrechnung
 * (Entwurf/Freigegeben/Beglichen/Ohne Abrechnung). Ebenso liefert der
 * Contract keinen „Offener Saldo je Host“ — der Saldo erscheint nur
 * aggregiert im Hero (meta.totals der Settlements-Liste), NICHT je Zeile.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { StatusChip } from '../../components/ui/StatusChip'
import { Pagination } from '../../components/Pagination'
import { formatNumber } from '../../components/ui/format'
import { HostFormDrawer, type HostDetailData } from './HostFormDrawer'
import {
  useHosts,
  useHost,
  useHostSplitRules,
  useHostsSettlements,
  useHotspotNameMap,
  buildHostRowData,
  type Host,
  type Settlement,
  type SettlementTotal,
  type HostRowData,
  type SettlementStatus,
} from './useHosts'
import './hosts.css'

const TONE_COUNT = 8
const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', EUR: '€', HNT: 'HNT' }

type PayoutFilter = 'all' | SettlementStatus | 'none'

// --- Formatierungs-Helfer ----------------------------------------------------

function fmtAmount(raw: string | undefined, currency: string | undefined): string | null {
  if (raw === undefined) return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  const symbol = currency ? (CURRENCY_SYMBOL[currency] ?? currency) : ''
  const num = formatNumber(value, 2)
  return symbol ? `${num} ${symbol}` : num
}

function fmtGb(raw: string | undefined): string | null {
  if (raw === undefined) return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  return formatNumber(value, 2)
}

/** "2026-07" → lokalisiert "Jul 2026" — bei unerwartetem Format Rohwert. */
function fmtPeriod(period: string | undefined, locale: string): string | null {
  if (!period) return null
  const match = /^(\d{4})-(\d{2})$/.exec(period)
  if (!match) return period
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

/** Chip-Kurzname wie in hosts.html: letztes Wort des Hotspot-Namens. */
function chipLabel(name: string): string {
  const words = name.trim().split(/\s+/)
  return words[words.length - 1] ?? name
}

// --- Auszahlungs-Status ------------------------------------------------------

function PayoutStatus({ status, hostId }: { status: SettlementStatus | 'none'; hostId: number }) {
  const { t } = useTranslation('hosts')
  const testId = `host-payout-${hostId}`

  // StatusChip (kind=quality) deckt success/warning ab; draft und "none"
  // sind neutrale Zustaende und rendern als schlichte Pill wie in der Vorlage.
  if (status === 'settled' || status === 'approved') {
    return (
      <StatusChip
        kind="quality"
        status={status === 'settled' ? 'good' : 'warning'}
        label={t(`payout.${status}.label`)}
        tooltip={t(`payout.${status}.tooltip`)}
        data-testid={testId}
      />
    )
  }
  return (
    <span
      className="pill"
      data-testid={testId}
      data-status={status}
      title={t(`payout.${status}.tooltip`)}
    >
      {t(`payout.${status}.label`)}
    </span>
  )
}

// --- Hero --------------------------------------------------------------------

function sumByCurrency(
  totals: SettlementTotal[] | undefined,
  statuses: SettlementStatus[],
): string[] {
  const byCurrency = new Map<string, number>()
  for (const total of totals ?? []) {
    if (!total.status || !statuses.includes(total.status)) continue
    const value = Number(total.total_amount ?? '')
    if (!Number.isFinite(value)) continue
    const cur = total.currency ?? ''
    byCurrency.set(cur, (byCurrency.get(cur) ?? 0) + value)
  }
  return [...byCurrency.entries()].map(([cur, value]) => {
    const symbol = CURRENCY_SYMBOL[cur] ?? cur
    return `${formatNumber(value, 2)} ${symbol}`
  })
}

/**
 * Hero. WICHTIG (F9): Die Betraege stammen aus `meta.totals` der
 * Abrechnungsliste und sind global; die Zaehler werden aus der geladenen
 * Seite (per_page=100) abgeleitet, weil der Contract keine Zaehler je Status
 * liefert. Ist die Liste gekappt (`settlementsTruncated`), sind die Zaehler
 * Untergrenzen und werden als solche beschriftet ("mindestens N") — statt
 * eine gekappte Zahl als exakte auszugeben.
 */
function HostsHero({
  hostCount,
  ruleCount,
  settlements,
  totals,
  settlementsTruncated,
}: {
  hostCount: number | undefined
  ruleCount: number | undefined
  settlements: Settlement[] | undefined
  totals: SettlementTotal[] | undefined
  settlementsTruncated: boolean
}) {
  const { t, i18n } = useTranslation('hosts')
  const noValue = t('hero.noValue')

  const openSums = sumByCurrency(totals, ['draft', 'approved'])
  const settledSums = sumByCurrency(totals, ['settled'])
  const openCount = (settlements ?? []).filter(
    (s) => s.status === 'draft' || s.status === 'approved',
  ).length
  const settledCount = (settlements ?? []).filter((s) => s.status === 'settled').length
  const latestPeriod = (settlements ?? []).reduce<string | undefined>(
    (max, s) => ((s.period_month ?? '') > (max ?? '') ? s.period_month : max),
    undefined,
  )

  return (
    <div className="hero-mesh hosts-hero">
      <div className="spread hosts-hero-inner">
        <div className="hosts-hero-copy">
          <div className="row hosts-hero-pills">
            <span className="pill pill-accent">
              {hostCount !== undefined ? t('hero.hostCount', { count: hostCount }) : noValue}
            </span>
            <span className="pill">
              {ruleCount !== undefined ? t('hero.ruleCount', { count: ruleCount }) : noValue}
            </span>
          </div>
          <h1>{t('title')}</h1>
          <p className="muted hosts-subtitle">{t('subtitle')}</p>
        </div>
        <div className="stat-strip hosts-stat-strip">
          <div className="stat">
            <div className="stat-label">{t('hero.openBalance')}</div>
            <div className="stat-value num-md c-warning">
              {openSums.length > 0 ? openSums.join(' · ') : noValue}
            </div>
            <div className="stat-delta muted" data-testid="hosts-hero-open-hint">
              {settlementsTruncated
                ? t('hero.openBalanceHintPartial', { count: openCount })
                : t('hero.openBalanceHint', { count: openCount })}
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">{t('hero.settled')}</div>
            <div className="stat-value num-md">
              {settledSums.length > 0 ? settledSums.join(' · ') : noValue}
            </div>
            <div className="stat-delta muted" data-testid="hosts-hero-settled-hint">
              {settlementsTruncated
                ? t('hero.settledHintPartial', { count: settledCount })
                : t('hero.settledHint', { count: settledCount })}
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">{t('hero.lastPeriod')}</div>
            <div className="stat-value num-md">
              {fmtPeriod(latestPeriod, i18n.language) ?? noValue}
            </div>
            <div className="stat-delta muted">
              <Link to="/settlements" className="hosts-hero-link">
                {t('hero.toSettlements')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// --- Zeile -------------------------------------------------------------------

function HostRow({
  host,
  index,
  rowData,
  hotspotNames,
  onOpen,
}: {
  host: Host
  index: number
  rowData: HostRowData | undefined
  hotspotNames: Map<string, string>
  onOpen: (host: Host) => void
}) {
  const { t, i18n } = useTranslation('hosts')
  const noValue = t('list.noValue')
  const name = host.name ?? noValue

  const since = host.created_at
    ? t('list.hostSince', {
        date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
          new Date(host.created_at),
        ),
      })
    : null

  const hotspotIds = rowData?.hotspotIds ?? []
  const settlement = rowData?.latestSettlement
  const status: SettlementStatus | 'none' = settlement?.status ?? 'none'

  const gb = fmtGb(settlement?.gigabytes_transferred)
  const amount = fmtAmount(settlement?.total_amount, settlement?.currency)
  const period = fmtPeriod(settlement?.period_month, i18n.language)

  const rowTone =
    status === 'settled' ? 'is-online' : status === 'none' ? '' : 'is-warn'

  return (
    <div
      className={`list-row hosts-row ${rowTone}`.trim()}
      role="button"
      tabIndex={0}
      aria-label={t('actions.openDetail', { name })}
      data-testid={`host-row-${host.id}`}
      onClick={() => onOpen(host)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(host)
        }
      }}
    >
      <div className="identity">
        <div className={`identity-img tone-${(index % TONE_COUNT) + 1}`}>
          <span>{initials(name)}</span>
        </div>
        <div className="identity-meta">
          <div className="identity-title">{name}</div>
          {since && <div className="identity-sub">{since}</div>}
        </div>
      </div>

      <div className="hosts-cell">
        <div className="hosts-cell-label">{t('list.colHotspots')}</div>
        {hotspotIds.length > 0 ? (
          <div className="row hosts-chip-row">
            {hotspotIds.slice(0, 3).map((id) => {
              const hotspotName = hotspotNames.get(String(id))
              return (
                <span className="hosts-hs-chip" key={id} title={hotspotName}>
                  {hotspotName ? chipLabel(hotspotName) : t('list.hotspotFallback', { id })}
                </span>
              )
            })}
            {hotspotIds.length > 3 && (
              <span className="hosts-hs-chip">
                {t('list.moreHotspots', { count: hotspotIds.length - 3 })}
              </span>
            )}
          </div>
        ) : (
          <div className="muted hosts-cell-empty">
            {t('list.noHotspots')}{' '}
            {host.id !== undefined && (
              <Link
                to="/splits/create"
                search={{ host_id: host.id }}
                className="hosts-detail-link"
                data-testid={`host-row-assign-${host.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                {t('list.assignHotspotCta')}
              </Link>
            )}
          </div>
        )}
      </div>

      <div className="hosts-cell">
        <div className="hosts-cell-label">{t('list.colMonthly')}</div>
        {gb !== null && amount !== null ? (
          <>
            <div className="num-sm hosts-monthly">
              {t('list.monthlyValue', { gb, amount })}
            </div>
            {period && (
              <div className="muted hosts-cell-sub">
                {t('list.monthlyPeriod', { period })}
              </div>
            )}
          </>
        ) : (
          <div className="muted hosts-cell-empty" title={t('list.gbUnavailable')}>
            {t('list.noSettlement')}
          </div>
        )}
      </div>

      <div className="hosts-cell hosts-cell-status">
        <div className="hosts-cell-label">{t('list.colStatus')}</div>
        <PayoutStatus status={status} hostId={host.id ?? index} />
      </div>
    </div>
  )
}

// --- Zustaende ---------------------------------------------------------------

function LoadingState() {
  const { t } = useTranslation('hosts')
  return (
    <div className="list" aria-busy="true" aria-label={t('loading')}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="list-row skeleton" aria-hidden="true" key={i}>
          <div className="skeleton-line" style={{ width: 220, height: 16 }} />
          <div className="skeleton-line" style={{ width: 120, height: 16 }} />
        </div>
      ))}
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('hosts')
  return (
    <div className="card hosts-empty" role="alert" data-testid="hosts-error">
      <p>{t('error.load')}</p>
      <button type="button" className="btn btn-primary hosts-empty-cta" onClick={onRetry}>
        {t('error.retry')}
      </button>
    </div>
  )
}

/** AC-3: Empty-State mit Call-to-Action zur Anlage des ersten Partners. */
function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation('hosts')
  return (
    <div className="card hosts-empty" data-testid="hosts-empty">
      <p className="hosts-empty-title">{t('empty.title')}</p>
      <p className="muted hosts-empty-hint">{t('empty.hint')}</p>
      <button
        type="button"
        className="btn btn-primary hosts-empty-cta"
        onClick={onCreate}
        data-testid="hosts-empty-cta"
      >
        {t('empty.cta')}
      </button>
    </div>
  )
}

// --- Seite -------------------------------------------------------------------

const PAYOUT_FILTERS: PayoutFilter[] = ['all', 'draft', 'approved', 'settled', 'none']

export function HostsPage({ hostId }: { hostId?: number }) {
  const { t } = useTranslation('hosts')
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<PayoutFilter>('all')
  const [createOpen, setCreateOpen] = useState(false)

  const { hosts, pagy, isLoading, isError, refetch } = useHosts(page)
  const { rules, ruleCount } = useHostSplitRules()
  const { settlements, totals, settlementsTruncated } = useHostsSettlements()
  const hotspotNames = useHotspotNameMap()

  // Deep-Link /hosts/:id — Detail unabhaengig von der geladenen Seite holen.
  const detail = useHost(hostId)

  const rowData = buildHostRowData(rules, settlements)

  // Story #23: Anreicherung fuer den Bearbeitungs-Drawer — ALLE Hotspots und
  // ALLE Abrechnungen dieses Hosts (nicht nur die juengste wie in der Zeile).
  const hostDetailData: HostDetailData | undefined =
    detail.host?.id !== undefined
      ? {
          hotspotIds: rowData.get(detail.host.id)?.hotspotIds ?? [],
          hotspotNames,
          // /api/v2/settlements kennt zwar einen host_id-Query-Param, die
          // Liste ist aber ohnehin schon per_page=100 geladen (fuer die
          // Zeilen-Anreicherung oben) — ein zweiter serverseitig gefilterter
          // Request wuerde nur dieselben Daten doppelt holen.
          settlements: (settlements ?? []).filter((s) => s.host_id === detail.host?.id),
          rules: (rules ?? []).filter((r) => r.host_id === detail.host?.id),
        }
      : undefined

  // F8: Suche und Status-Filter laufen rein clientseitig ueber die GELADENE
  // Seite — GET /api/v2/hosts kennt im Contract weder einen Such- noch einen
  // Status-Parameter (Status kommt ohnehin erst aus den Settlements).
  // TODO/Follow-up: sobald der Contract `q`/`status` fuer /api/v2/hosts
  // anbietet, Filterung serverseitig ausfuehren und diesen Hinweis entfernen.
  const filtered = (hosts ?? []).filter((host) => {
    if (query && !(host.name ?? '').toLowerCase().includes(query.toLowerCase())) {
      return false
    }
    if (filter === 'all') return true
    const status = host.id !== undefined
      ? (rowData.get(host.id)?.latestSettlement?.status ?? 'none')
      : 'none'
    return status === filter
  })

  const hasHosts = !isLoading && !isError && !!hosts && hosts.length > 0
  const isEmpty = !isLoading && !isError && !!hosts && hosts.length === 0 && page === 1

  const isFiltering = query.trim() !== '' || filter !== 'all'
  const currentPage = pagy?.page ?? page
  const totalPages = pagy?.pages ?? 1
  /** Filter greift nur auf dieser Seite, es gibt aber weitere Seiten. */
  const isPagedFiltering = isFiltering && totalPages > 1

  const openDrawerFor = (host: Host) => {
    if (host.id !== undefined) {
      void navigate({ to: '/hosts/$id', params: { id: String(host.id) } })
    }
  }
  const closeDrawer = () => {
    setCreateOpen(false)
    if (hostId !== undefined) void navigate({ to: '/hosts' })
  }

  return (
    <section data-testid="hosts-page">
      <HostsHero
        hostCount={pagy?.count ?? hosts?.length}
        // Echtes Gesamt aus meta.pagy, nicht die Laenge der geladenen Seite.
        ruleCount={ruleCount ?? rules?.length}
        settlements={settlements}
        totals={totals}
        settlementsTruncated={settlementsTruncated}
      />

      <div className="filterbar hosts-filterbar">
        <div className="hosts-search">
          <Search aria-hidden="true" size={14} className="hosts-search-icon" />
          <input
            className="input search-input hosts-search-input"
            placeholder={t('filter.searchPlaceholder')}
            aria-label={t('filter.searchAria')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="hosts-search"
          />
        </div>
        <div
          className="segment hosts-segment"
          role="group"
          aria-label={t('filter.statusAria')}
        >
          {PAYOUT_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={filter === f ? 'is-active' : undefined}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              data-testid={`hosts-filter-${f}`}
            >
              {t(`filter.${f}`)}
            </button>
          ))}
        </div>
        <div className="hosts-filterbar-spacer" />
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => setCreateOpen(true)}
          data-testid="hosts-create"
        >
          {t('actions.create')}
        </button>
      </div>

      {isLoading && <LoadingState />}
      {isError && <ErrorState onRetry={refetch} />}
      {isEmpty && <EmptyState onCreate={() => setCreateOpen(true)} />}

      {hasHosts && isPagedFiltering && (
        <div className="hint hosts-scope-hint" role="status" data-testid="hosts-filter-scope">
          {t('filter.scopeHint', { page: currentPage, pages: totalPages })}
        </div>
      )}

      {hasHosts && (
        <>
          <div className="list" role="list" data-testid="hosts-list">
            {filtered.map((host, i) => (
              <HostRow
                key={host.id ?? i}
                host={host}
                index={i}
                rowData={host.id !== undefined ? rowData.get(host.id) : undefined}
                hotspotNames={hotspotNames}
                onOpen={openDrawerFor}
              />
            ))}
            {filtered.length === 0 && (
              <div className="hint" data-testid="hosts-no-match">
                {isPagedFiltering
                  ? t('filter.noMatchOnPage', { page: currentPage, pages: totalPages })
                  : t('filter.noMatch')}
              </div>
            )}
          </div>
          <Pagination
            page={pagy?.page ?? page}
            pages={pagy?.pages ?? 1}
            count={pagy?.count ?? filtered.length}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Anlage-Drawer (AC-3-CTA und Filterbar-Button) */}
      <HostFormDrawer open={createOpen} onClose={closeDrawer} />

      {/* Bearbeitungs-Drawer via Deep-Link /hosts/:id (AC-2) */}
      {hostId !== undefined && detail.isError && (
        <div className="card hosts-empty" role="alert" data-testid="hosts-detail-error">
          <p>{t('error.detailNotFound')}</p>
          <button type="button" className="btn hosts-empty-cta" onClick={closeDrawer}>
            {t('error.backToList')}
          </button>
        </div>
      )}
      <HostFormDrawer
        open={hostId !== undefined && !!detail.host}
        host={detail.host}
        detail={hostDetailData}
        onClose={closeDrawer}
      />
    </section>
  )
}
